import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  IconButton,

} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';


const EMPLOYMENT_TYPES = [
  'Vollzeit',
  'Teilzeit',
  'Minijob',
  'Werkstudent',
  'Ausbildung',
  'Praktikum'
];


function Employees() {
  console.log('Employees component rendering');

  const [employees, setEmployees] = useState([]);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingEmployee, setEditingEmployee] = useState(null);

  // Form state

  const [formData, setFormData] = useState({
    name: '',
    employment_type: '',
    hours_per_week: '38,50',
    group_id: ''
  });

  const resetForm = () => {
    console.log('Resetting form data');
    setFormData({
      name: '',
      employment_type: '',
      hours_per_week: '38,50',
      group_id: ''
    });
    setEditingEmployee(null);
  };

  useEffect(() => {
    console.log('Employees component mounted');
    fetchEmployees();
    fetchGroups();
  }, []);

  const fetchEmployees = async () => {
    console.log('Fetching employees...');
    try {
      const response = await fetch('/api/employees');
      console.log('Employees API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response text:', errorText);
        throw new Error('Failed to fetch employees');
      }
      
      const data = await response.json();
      console.log('Fetched employees data:', data);
      setEmployees(data);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setError('Fehler beim Laden der Mitarbeiter');
    }
  };

  const fetchGroups = async () => {
    console.log('Fetching groups...');
    try {
      const response = await fetch('/api/groups');
      console.log('Groups API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response text:', errorText);
        throw new Error('Failed to fetch groups');
      }
      
      const data = await response.json();
      console.log('Fetched groups data:', data);
      setGroups(data);
    } catch (err) {
      console.error('Error fetching groups:', err);
      setError('Fehler beim Laden der Gruppen');
    }
  };

  const handleEdit = (employee) => {
    console.log('Editing employee:', employee);
    setEditingEmployee(employee);
    setFormData({
      name: employee.name,
      employment_type: employee.employment_type,
      hours_per_week: employee.hours_per_week,
      group_id: employee.group_id || ''
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    console.log('Submitting employee form with data:', formData);
    
    if (!formData.name || !formData.employment_type || !formData.hours_per_week) {
      console.log('Form validation failed - missing required fields');
      setError('Name, Anstellungsart und Stunden pro Woche sind erforderlich');
      return;
    }

    try {
      const method = editingEmployee ? 'PUT' : 'POST';
      const url = editingEmployee 
        ? `/api/employees/${editingEmployee.id}`
        : '/api/employees';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      console.log('Employee save response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response text:', errorText);
        throw new Error('Failed to save employee');
      }

      const result = await response.json();
      console.log('Employee saved successfully:', result);
      setSuccessMessage(editingEmployee 
        ? 'Mitarbeiter erfolgreich aktualisiert'
        : 'Mitarbeiter erfolgreich erstellt'
      );
      setOpen(false);
      resetForm();
      fetchEmployees();
    } catch (err) {
      console.error('Error saving employee:', err);
      setError('Fehler beim Speichern des Mitarbeiters');
    }
  };

  console.log('Current state:', {
    employeesCount: employees.length,
    groupsCount: groups.length,
    dialogOpen: open,
    error,
    successMessage
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" color="text.primary">
          <PeopleIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
          Mitarbeiter
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            console.log('Opening new employee dialog');
            resetForm();
            setOpen(true);
          }}
        >
          Mitarbeiter anlegen
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Anstellungsart</TableCell>
              <TableCell>Std/Woche</TableCell>
              <TableCell>Gruppe</TableCell>

              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.map((employee) => (
              <TableRow key={employee.id}>
                <TableCell>{employee.name}</TableCell>
                <TableCell>{employee.employment_type}</TableCell>
                <TableCell>{employee.hours_per_week}</TableCell>
                <TableCell>
                  {employee.group_id 
                    ? groups.find(g => g.id === employee.group_id)?.name 
                    : 'Keine feste Gruppe'}
                </TableCell>

                <TableCell>
                  <IconButton 
                    size="small" 
                    onClick={() => handleEdit(employee)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog 
        open={open} 
        onClose={() => {
          console.log('Closing employee dialog');
          setOpen(false);
          resetForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingEmployee ? 'Mitarbeiter bearbeiten' : 'Mitarbeiter anlegen'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              fullWidth
              value={formData.name}
              onChange={(e) => {
                console.log('Name field changed:', e.target.value);
                setFormData({ ...formData, name: e.target.value });
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Anstellungsart</InputLabel>
              <Select
                value={formData.employment_type}
                label="Anstellungsart"
                onChange={(e) => {
                  console.log('Employment type changed:', e.target.value);
                  setFormData({ ...formData, employment_type: e.target.value });
                }}
              >
                {EMPLOYMENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Stunden pro Woche"
              fullWidth
              placeholder="38,50"
              value={formData.hours_per_week}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9,]/g, '');
                console.log('Hours changed:', value);
                setFormData({ ...formData, hours_per_week: value });
              }}
              helperText="Format: 38,50"
            />

            <FormControl fullWidth>
              <InputLabel>Gruppe</InputLabel>
              <Select
                value={formData.group_id}
                label="Gruppe"
                onChange={(e) => {
                  console.log('Group changed:', e.target.value);
                  setFormData({ ...formData, group_id: e.target.value });
                }}
              >
                <MenuItem value="">Keine feste Gruppe</MenuItem>
                {groups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
                ))}
              </Select>
            </FormControl>


          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            console.log('Canceling employee form');
            setOpen(false);
            resetForm();
          }}>
            Abbrechen
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingEmployee ? 'Speichern' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Employees;